/**
 * Templates and Prompts
 * 
 * This file contains all template texts and prompts used throughout the application.
 * Centralizing these makes it easier to modify them and potentially generate them
 * dynamically based on user context.
 */

// Default sample prompts for different diagram types
export const DEFAULT_SAMPLE_PROMPTS = [
  {
    title: "Gantt Chart for Project Management",
    prompt: "Create a Gantt chart for project management"
  },
  {
    title: "Gantt Chart for Task Scheduling",
    prompt: "Create a Gantt chart for task scheduling"
  },
  {
    title: "Gantt Chart for Event Planning",
    prompt: "Create a Gantt chart for event planning"
  },
  {
    title: "Gantt Chart for Production Timeline",
    prompt: "Create a Gantt chart for production timeline"
  },
  {
    title: "Flowchart for Sales Process",
    prompt: "Create a flowchart for a sales process"
  },
  {
    title: "Sequence Diagram for API Interaction",
    prompt: "Create a sequence diagram for API interaction"
  },
  {
    title: "Class Diagram for System Architecture",
    prompt: "Create a class diagram for system architecture"
  },
  {
    title: "Entity Relationship Diagram for Database",
    prompt: "Create an entity relationship diagram for a database"
  },
  {
    title: "Mind Map for Project Planning",
    prompt: "Create a mind map for project planning"
  },
  {
    title: "Quadrant Chart for Product Analysis",
    prompt: "Create a quadrant chart for product analysis"
  },
  {
    title: "Quadrant Chart for Risk Assessment",
    prompt: "Create a quadrant chart for risk assessment with impact vs probability"
  }
];

// Function to get sample prompts - can be personalized or default
export const getSamplePrompts = (personalizedPrompts = []) => {
  // If we have personalized prompts, use them
  if (personalizedPrompts && personalizedPrompts.length > 0) {
    return personalizedPrompts;
  }
  
  // Otherwise, return the default prompts
  return DEFAULT_SAMPLE_PROMPTS;
};

// For backward compatibility, export SAMPLE_PROMPTS as the default prompts
export const SAMPLE_PROMPTS = DEFAULT_SAMPLE_PROMPTS;

// Welcome screen text
export const WELCOME_TEXT = {
  header: "Diagram Assistant",
  description: "Welcome to the AI-powered Diagram Assistant! I can help you create and improve Mermaid diagrams.",
  helpText: "Select a template below or type your own question to get started."
};

// Input placeholder text
export const INPUT_PLACEHOLDER = "Ask about diagrams or request changes...";

// System messages for API
export const SYSTEM_MESSAGES = {
  diagramContext: (currentCode) => `Current Mermaid diagram code:\n\`\`\`mermaid\n${currentCode}\n\`\`\`\nPlease modify this diagram based on the user's request. Make sure to escape parentheses in text with backslashes, e.g., "Text with \\(parentheses\\)" or [Text with \\(parentheses\\)].`
};

// Error messages
export const ERROR_MESSAGES = {
  apiConnection: "Sorry, I'm having trouble connecting to the backend API. Please check your connection and try again.",
  apiError: "Sorry, I encountered an error while processing your request. Please try again.",
  apiUnavailable: "Unable to connect to the Diagram Assistant API. Please check that the backend server is running."
};

// Templates section titles
export const TEMPLATE_TITLES = {
  preSalesDiagrams: "Pre-Sales Diagram Templates"
};

// Initial query template for when a user selects code
export const INITIAL_QUERY_TEMPLATE = (code) => {
  // Ensure the code doesn't already have markdown markers
  const cleanCode = code.replace(/```mermaid/g, '').replace(/```/g, '').trim();
  return `\`\`\`\n${cleanCode}\n\`\`\``;
};

// Diagram types mapping
export const DIAGRAM_TYPES = [
  { prefix: 'flowchart', type: 'flowchart' },
  { prefix: 'graph', type: 'flowchart' },
  { prefix: 'sequencediagram', type: 'sequence diagram' },
  { prefix: 'classdiagram', type: 'class diagram' },
  { prefix: 'erdiagram', type: 'entity relationship diagram' },
  { prefix: 'gantt', type: 'gantt chart' },
  { prefix: 'pie', type: 'pie chart' },
  { prefix: 'statediagram', type: 'state diagram' },
  { prefix: 'journey', type: 'user journey' },
  { prefix: 'mindmap', type: 'mind map' },
  { prefix: 'quadrantchart', type: 'quadrant chart' },
  { prefix: 'kanban', type: 'kanban board' }
];

// Example templates for different diagram types, organized by pre-sales use cases
export const EXAMPLE_TEMPLATES = [
  // Sales Process Templates
  {
    category: "Sales Process",
    templates: [
      {
        name: 'Sales Pipeline',
        icon: 'FiBarChart2',
        code: `flowchart LR
    Lead[New Lead] --> Qualify{Qualify?}
    Qualify -->|Yes| Demo[Product Demo]
    Qualify -->|No| Nurture[Lead Nurturing]
    Demo --> Proposal[Send Proposal]
    Proposal --> Close{Closed?}
    Close -->|Won| Customer[New Customer]
    Close -->|Lost| Feedback[Get Feedback]
    Feedback --> Improve[Improve Process]
    
    classDef green fill:#d1fae5,stroke:#059669,stroke-width:2px,color:#065f46
    classDef blue fill:#dbeafe,stroke:#2563eb,stroke-width:2px,color:#1e40af
    classDef red fill:#fee2e2,stroke:#dc2626,stroke-width:2px,color:#991b1b
    classDef yellow fill:#fef3c7,stroke:#d97706,stroke-width:2px,color:#92400e
    
    class Lead,Customer green
    class Demo,Proposal blue
    class Nurture,Feedback yellow
    class Qualify,Close red`
      },
      {
        name: 'Customer Journey',
        icon: 'FiActivity',
        code: `journey
    title Customer Journey Map
    section Awareness
      Discover product: 3: Customer
      Research options: 4: Customer
    section Consideration
      Compare features: 5: Customer
      Read reviews: 3: Customer
      Request demo: 5: Customer, Sales
    section Decision
      Receive proposal: 3: Customer, Sales
      Negotiate terms: 3: Customer, Sales
      Sign contract: 5: Customer, Sales
    section Onboarding
      Implementation: 5: Customer, Support
      Training: 4: Customer, Support
    section Retention
      Regular usage: 4: Customer
      Support requests: 3: Customer, Support
      Renewal discussion: 5: Customer, Sales`
      },
      {
        name: 'Decision Flow',
        icon: 'FiShuffle',
        code: `flowchart TD
    A[Business Need] --> B{Solution Required?}
    B -->|Yes| C[Identify Requirements]
    B -->|No| D[Monitor Situation]
    C --> E[Research Solutions]
    E --> F[Internal Review]
    F --> G{Budget Approval?}
    G -->|Yes| H[Vendor Selection]
    G -->|No| I[Revise Scope]
    I --> F
    H --> J[Implementation]
    J --> K[Evaluation]
    
    classDef decision fill:#f8fafc,stroke:#475569,stroke-width:2px,color:#1e293b
    classDef process fill:#f0f9ff,stroke:#0369a1,stroke-width:2px,color:#0c4a6e
    classDef start fill:#ecfdf5,stroke:#059669,stroke-width:2px,color:#065f46
    
    class A,J,K start
    class B,G decision
    class C,D,E,F,H,I process`
      },
      {
        name: 'Sales Funnel Flow',
        icon: 'FiActivity',
        description: 'Visualize your sales funnel with a Sankey diagram',
        code: `sankey-beta
Marketing, MQL, 1000
Marketing, Website Visitors, 5000
Marketing, Social Media, 3000

MQL, SQL, 500
MQL, Disqualified, 500

SQL, Discovery Call, 400
SQL, No Response, 100

Discovery Call, Product Demo, 300
Discovery Call, Not Interested, 100

Product Demo, Proposal, 250
Product Demo, Competitor, 50

Proposal, Negotiation, 200
Proposal, Declined, 50

Negotiation, Closed Won, 150
Negotiation, Closed Lost, 50

Website Visitors, Contact Form, 200
Website Visitors, Bounce, 4800

Social Media, MQL, 300
Social Media, Brand Awareness, 2700`
      }
    ]
  },
  
  // Solution Architecture Templates
  {
    category: "Solution Architecture",
    templates: [
      {
        name: 'System Architecture',
        icon: 'FiServer',
        code: `flowchart TB
    subgraph Client Layer
      Browser[Web Browser]
      Mobile[Mobile App]
    end
    
    subgraph Load Balancing
      LB[Load Balancer]
    end
    
    subgraph Application Layer
      API[API Gateway]
      Auth[Authentication]
      Services[Microservices]
    end
    
    subgraph Data Layer
      DB[(Primary Database)]
      Cache[(Redis Cache)]
      Search[(Search Index)]
    end
    
    Browser --> LB
    Mobile --> LB
    LB --> API
    API --> Auth
    API --> Services
    Services --> DB
    Services --> Cache
    Services --> Search
    
    classDef client fill:#dbeafe,stroke:#2563eb,stroke-width:2px,color:#1e40af
    classDef app fill:#f0fdf4,stroke:#16a34a,stroke-width:2px,color:#166534
    classDef data fill:#fef3c7,stroke:#d97706,stroke-width:2px,color:#92400e
    classDef lb fill:#f3e8ff,stroke:#9333ea,stroke-width:2px,color:#6b21a8
    
    class Browser,Mobile client
    class API,Auth,Services app
    class DB,Cache,Search data
    class LB lb`
      },
      {
        name: 'C4 System Context',
        icon: 'FiBox',
        code: `C4Context
    title System Context Diagram for Enterprise Solution
    
    Person(customer, "Customer", "A customer of the enterprise with an account")
    Person(admin, "Administrator", "System administrator with full access")
    
    System(system, "Enterprise System", "Allows customers to manage their accounts and administrators to manage the system")
    
    System_Ext(email, "Email System", "Sends emails to customers")
    System_Ext(payment, "Payment Gateway", "Processes payments")
    
    Rel(customer, system, "Uses", "HTTPS")
    Rel(admin, system, "Manages", "HTTPS")
    Rel(system, email, "Sends emails using", "SMTP")
    Rel(system, payment, "Processes payments using", "API")`
      },
      {
        name: 'Class Diagram',
        icon: 'FiCpu',
        code: `classDiagram
    class Customer {
      +String id
      +String name
      +String email
      +Date createdAt
      +subscribe()
      +unsubscribe()
    }
    
    class Subscription {
      +String id
      +String customerId
      +String planId
      +Date startDate
      +Date endDate
      +Boolean isActive
      +renew()
      +cancel()
    }
    
    class Plan {
      +String id
      +String name
      +Number price
      +String billingCycle
      +String[] features
      +createPlan()
      +updatePlan()
    }
    
    class Invoice {
      +String id
      +String customerId
      +String subscriptionId
      +Number amount
      +Date dueDate
      +Boolean isPaid
      +pay()
      +void()
    }
    
    Customer "1" -- "0..*" Subscription : has
    Subscription "0..*" -- "1" Plan : uses
    Customer "1" -- "0..*" Invoice : receives`
      },
      {
        name: 'Entity Relationship',
        icon: 'FiDatabase',
        code: `erDiagram
    CUSTOMER ||--o{ ORDER : places
    CUSTOMER {
        string id PK
        string name
        string email
        string phone
        date createdAt
    }
    ORDER ||--|{ ORDER_ITEM : contains
    ORDER {
        string id PK
        string customerId FK
        decimal total
        string status
        date orderDate
    }
    PRODUCT ||--o{ ORDER_ITEM : "ordered in"
    PRODUCT {
        string id PK
        string name
        string description
        decimal price
        int stockQuantity
    }
    ORDER_ITEM {
        string orderId FK
        string productId FK
        int quantity
        decimal price
    }`
      }
    ]
  },
  
  // Project Planning Templates
  {
    category: "Project Planning",
    templates: [
      {
        name: 'Project Timeline',
        icon: 'FiCalendar',
        code: `gantt
    title Project Implementation Timeline
    dateFormat  YYYY-MM-DD
    
    section Discovery
    Requirements gathering    :a1, 2023-06-01, 14d
    Current state assessment  :a2, after a1, 10d
    
    section Planning
    Solution design           :a3, after a2, 14d
    Architecture review       :a4, after a3, 7d
    Implementation plan       :a5, after a4, 7d
    
    section Development
    Environment setup         :a6, after a5, 5d
    Core implementation       :a7, after a6, 21d
    Integration development   :a8, after a7, 14d
    
    section Testing
    Unit testing              :a9, after a8, 7d
    Integration testing       :a10, after a9, 10d
    User acceptance testing   :a11, after a10, 14d
    
    section Deployment
    Training                  :a12, after a11, 7d
    Go-live preparation       :a13, after a12, 5d
    Production deployment     :a14, after a13, 2d
    Post-deployment support   :a15, after a14, 14d`
      },
      {
        name: 'Implementation Phases',
        icon: 'FiLayers',
        code: `flowchart TB
    subgraph Phase 1
      direction TB
      P1_1[Requirements Analysis]
      P1_2[Proof of Concept]
      P1_3[Initial Deployment]
      P1_1 --> P1_2 --> P1_3
    end
    
    subgraph Phase 2
      direction TB
      P2_1[Core Features]
      P2_2[Data Migration]
      P2_3[User Training]
      P2_1 --> P2_2 --> P2_3
    end
    
    subgraph Phase 3
      direction TB
      P3_1[Advanced Features]
      P3_2[Integration]
      P3_3[Performance Tuning]
      P3_1 --> P3_2 --> P3_3
    end
    
    subgraph Phase 4
      direction TB
      P4_1[Monitoring Setup]
      P4_2[Documentation]
      P4_3[Handover]
      P4_1 --> P4_2 --> P4_3
    end
    
    P1_3 --> P2_1
    P2_3 --> P3_1
    P3_3 --> P4_1
    
    classDef phase1 fill:#dbeafe,stroke:#2563eb,stroke-width:2px,color:#1e40af
    classDef phase2 fill:#dcfce7,stroke:#16a34a,stroke-width:2px,color:#166534
    classDef phase3 fill:#fef3c7,stroke:#d97706,stroke-width:2px,color:#92400e
    classDef phase4 fill:#f3e8ff,stroke:#9333ea,stroke-width:2px,color:#6b21a8
    
    class P1_1,P1_2,P1_3 phase1
    class P2_1,P2_2,P2_3 phase2
    class P3_1,P3_2,P3_3 phase3
    class P4_1,P4_2,P4_3 phase4`
      },
      {
        name: 'Resource Allocation',
        icon: 'FiUsers',
        code: `pie
    title Resource Allocation by Department
    "Implementation Team" : 40
    "Customer Success" : 20
    "Development" : 25
    "QA" : 10
    "Management" : 5`
      },
      {
        name: 'Project Kanban',
        icon: 'FiTrello',
        code: `kanban
    To Do
    Gather requirements
    Create project plan
    
    In Progress
    Design architecture
    Setup development environment
    
    Code Review
    Implement authentication
    Build API endpoints
    
    Testing
    Unit tests
    Integration tests
    
    Done
    Project kickoff
    Stakeholder interviews`
      },
      {
        name: 'Resource Allocation Flow',
        icon: 'FiActivity',
        description: 'Visualize resource allocation across project phases',
        code: `sankey-beta
Budget, Development, 500000
Budget, Marketing, 300000
Budget, Operations, 200000
Budget, Research, 150000
Budget, Administration, 100000

Development, Frontend, 200000
Development, Backend, 150000
Development, QA Testing, 100000
Development, DevOps, 50000

Marketing, Digital Ads, 150000
Marketing, Content Creation, 80000
Marketing, Events, 70000

Operations, Infrastructure, 120000
Operations, Support, 80000

Research, Market Analysis, 90000
Research, Product Research, 60000

Administration, HR, 60000
Administration, Finance, 40000`
      }
    ]
  },
  
  // Business Analysis Templates
  {
    category: "Business Analysis",
    templates: [
      {
        name: 'State Diagram',
        icon: 'FiShuffle',
        code: `stateDiagram-v2
    [*] --> LeadGenerated
    LeadGenerated --> LeadQualified: Qualification
    LeadGenerated --> LeadDisqualified: Not a fit
    LeadQualified --> ProposalSent: Send proposal
    ProposalSent --> Negotiation: Client responds
    ProposalSent --> Lost: No response
    Negotiation --> ContractSent: Agreement reached
    Negotiation --> ProposalRevision: Request changes
    Negotiation --> Lost: Client declines
    ProposalRevision --> ProposalSent: Send revised
    ContractSent --> Won: Contract signed
    ContractSent --> Lost: Contract rejected
    Won --> [*]
    Lost --> [*]
    LeadDisqualified --> [*]
    
    state LeadQualified {
      [*] --> NeedsIdentified
      NeedsIdentified --> BudgetConfirmed
      BudgetConfirmed --> TimelineEstablished
      TimelineEstablished --> DecisionMakerEngaged
      DecisionMakerEngaged --> [*]
    }`
      },
      {
        name: 'Quadrant Chart',
        icon: 'FiTarget',
        code: `quadrantChart
    title Feature Prioritization Matrix
    x-axis Low Impact --> High Impact
    y-axis Low Effort --> High Effort
    quadrant-1 Quick Wins
    quadrant-2 Major Projects
    quadrant-3 Time Sinks
    quadrant-4 Fill-ins
    
    "User Authentication": [0.7, 0.6]
    "Dashboard Redesign": [0.9, 0.8]
    "Email Notifications": [0.5, 0.3]
    "Mobile App": [0.8, 0.9]
    "Search Functionality": [0.6, 0.4]
    "API Integration": [0.7, 0.7]
    "Reporting Module": [0.9, 0.5]
    "User Onboarding": [0.4, 0.5]
    "Admin Panel": [0.6, 0.8]
    "Data Export": [0.4, 0.3]`
      },
      {
        name: 'Mindmap',
        icon: 'FiMap',
        code: `mindmap
    root((Enterprise Solution))
      Core Features
        User Management
          Authentication
          Authorization
          User Profiles
        Data Management
          Storage
          Retrieval
          Backup
        Reporting
          Standard Reports
          Custom Reports
          Dashboards
      Integrations
        CRM Systems
        ERP Systems
        Payment Gateways
        Email Services
      Deployment Options
        Cloud Hosted
          AWS
          Azure
          Google Cloud
        On-Premises
        Hybrid
      Support Services
        Implementation
        Training
        Maintenance
        Upgrades`
      },
      {
        name: 'Requirement Diagram',
        icon: 'FiCheckSquare',
        code: `requirementDiagram
    requirement high_level_req {
      id: 1
      text: The system shall provide enterprise-grade security
      risk: high
      verifymethod: test
    }
    
    element security_system {
      type: system
      docref: security.md
    }
    
    requirement auth_req {
      id: 1.1
      text: The system shall authenticate users
      risk: medium
      verifymethod: demonstration
    }
    
    requirement access_control_req {
      id: 1.2
      text: The system shall authorize user actions
      risk: medium
      verifymethod: demonstration
    }
    
    requirement encryption_req {
      id: 1.3
      text: All data shall be encrypted at rest and in transit
      risk: high
      verifymethod: test
    }
    
    high_level_req <- auth_req
    high_level_req <- access_control_req
    high_level_req <- encryption_req
    
    security_system - satisfies -> high_level_req`
      },
      {
        name: 'XY Chart',
        icon: 'FiTrendingUp',
        code: `xychart-beta
    title "ROI Projection Over Time"
    x-axis [Q1, Q2, Q3, Q4, Q5, Q6, Q7, Q8]
    y-axis "Return on Investment (%)" 0 --> 100
    
    line [10, 20, 30, 40, 50, 55, 60, 70]
    line [5, 15, 25, 35, 45, 55, 65, 75]
    
    bar [8, 18, 28, 38, 48, 58, 68, 78]`
      }
    ]
  },
  
  // Technical Documentation Templates
  {
    category: "Technical Documentation",
    templates: [
      {
        name: 'Sequence Diagram',
        icon: 'FiShare2',
        code: `sequenceDiagram
    participant User
    participant Frontend
    participant API
    participant Auth
    participant DB
    
    User->>Frontend: Access application
    Frontend->>API: Request data
    API->>Auth: Validate token
    
    alt Valid token
        Auth->>API: Token valid
        API->>DB: Query data
        DB->>API: Return data
        API->>Frontend: Return response
        Frontend->>User: Display data
    else Invalid token
        Auth->>API: Token invalid
        API->>Frontend: Authentication error
        Frontend->>User: Show login screen
        User->>Frontend: Enter credentials
        Frontend->>API: Login request
        API->>Auth: Verify credentials
        Auth->>API: Generate new token
        API->>Frontend: Return token
        Frontend->>User: Show authenticated view
    end`
      },
      {
        name: 'Git Workflow',
        icon: 'FiGitBranch',
        code: `gitGraph
    commit id: "initial"
    branch develop
    checkout develop
    commit id: "feature-start"
    branch feature/login
    checkout feature/login
    commit id: "login-1"
    commit id: "login-2"
    checkout develop
    merge feature/login
    branch feature/dashboard
    checkout feature/dashboard
    commit id: "dashboard-1"
    commit id: "dashboard-2"
    checkout develop
    merge feature/dashboard
    checkout main
    merge develop tag: "v1.0.0"
    branch hotfix/login
    checkout hotfix/login
    commit id: "fix-login"
    checkout main
    merge hotfix/login tag: "v1.0.1"
    checkout develop
    merge main`
      },
      {
        name: 'Block Diagram',
        icon: 'FiPackage',
        code: `block-beta
    columns 3
    
    block:Core System
      columns 1
      
      block:Frontend
        UI Components
        State Management
        API Client
      end
      
      block:Backend
        API Gateway
        Business Logic
        Data Access
      end
      
      block:Database
        User Data
        Transaction Data
        Audit Logs
      end
      
      space
      
      Frontend -- API Client --> Backend
      Backend -- Data Access --> Database
    end
    
    block:External Systems
      columns 1
      
      Payment Gateway
      Email Service
      Analytics Platform
      
      space
      
      Backend -.-> Payment Gateway
      Backend -.-> Email Service
      Backend -.-> Analytics Platform
    end
    
    block:Infrastructure
      columns 1
      
      Cloud Hosting
      CDN
      Load Balancer
      Monitoring
      
      space
      
      Frontend --> CDN
      Backend --> Load Balancer
      Database --> Cloud Hosting
      Core System --> Monitoring
    end`
      },
      {
        name: 'Sankey Diagram',
        icon: 'FiActivity',
        code: `sankey-beta
User Acquisition, Website, 100
User Acquisition, Referral, 20
User Acquisition, Advertising, 80

Website, Free Trial, 60
Website, Documentation, 40

Referral, Free Trial, 15
Referral, Paid Subscription, 5

Advertising, Free Trial, 50
Advertising, Paid Subscription, 30

Free Trial, Paid Subscription, 45
Free Trial, Churn, 80

Documentation, Free Trial, 30
Documentation, Paid Subscription, 10

Paid Subscription, Renewal, 70
Paid Subscription, Churn, 20`
      },
      {
        name: 'Documentation',
        icon: 'FiFileText',
        code: `flowchart LR
    subgraph Documentation Structure
      direction TB
      D1[Overview]
      D2[Getting Started]
      D3[Core Concepts]
      D4[API Reference]
      D5[Tutorials]
      D6[Troubleshooting]
      
      D1 --> D2
      D2 --> D3
      D3 --> D4
      D2 --> D5
      D5 --> D6
    end
    
    subgraph User Types
      direction TB
      U1[New Users]
      U2[Developers]
      U3[Administrators]
      U4[Support Team]
    end
    
    U1 --> D1
    U1 --> D2
    U1 --> D5
    U2 --> D3
    U2 --> D4
    U2 --> D5
    U3 --> D1
    U3 --> D3
    U3 --> D6
    U4 --> D6
    
    classDef docs fill:#dbeafe,stroke:#2563eb,stroke-width:2px,color:#1e40af
    classDef users fill:#dcfce7,stroke:#16a34a,stroke-width:2px,color:#166534
    
    class D1,D2,D3,D4,D5,D6 docs
    class U1,U2,U3,U4 users`
      }
    ]
  }
];

// Sample diagrams that users can load directly
export const EXAMPLE_DIAGRAMS = [
  {
    title: "Simple Flowchart",
    type: "flowchart",
    description: "Basic process flow with decision points",
    code: `flowchart TD
    A[Start] --> B{Is it working?}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B`
  },
  {
    title: "Project Timeline",
    type: "gantt",
    description: "Project management timeline with milestones",
    code: `gantt
    title Project Timeline
    dateFormat  YYYY-MM-DD
    section Planning
    Requirements gathering  :a1, 2023-01-01, 10d
    System design           :a2, after a1, 15d
    section Development
    Frontend implementation :b1, after a2, 20d
    Backend implementation  :b2, after a2, 25d
    section Testing
    Integration testing     :c1, after b1, 7d
    User acceptance testing :c2, after c1, 7d
    section Deployment
    Training                :d1, after c2, 5d
    Go Live                 :milestone, after d1, 0d`
  },
  {
    title: "Organization Chart",
    type: "flowchart",
    description: "Hierarchical organization structure",
    code: `flowchart TD
    CEO[CEO\\nJohn Smith] --> CTO[CTO\\nJane Doe]
    CEO --> CFO[CFO\\nMike Johnson]
    CEO --> COO[COO\\nSarah Williams]
    CTO --> Dev1[Lead Developer\\nAlex Brown]
    CTO --> Dev2[DevOps Engineer\\nChris Lee]
    CFO --> Fin1[Financial Analyst\\nTom Davis]
    COO --> PM[Product Manager\\nEmily Wilson]
    
    classDef executive fill:#f9d5e5,stroke:#333,stroke-width:1px;
    classDef management fill:#eeeeee,stroke:#333,stroke-width:1px;
    classDef staff fill:#d5e8f9,stroke:#333,stroke-width:1px;
    
    class CEO,CTO,CFO,COO executive;
    class Dev1,PM management;
    class Dev2,Fin1 staff;`
  },
  {
    title: "Sequence Diagram",
    type: "sequence",
    description: "API interaction flow between systems",
    code: `sequenceDiagram
    participant User
    participant Frontend
    participant API
    participant Database
    
    User->>Frontend: Submit form
    Frontend->>API: POST /submit
    API->>API: Validate data
    API->>Database: Store data
    Database-->>API: Confirm success
    API-->>Frontend: Return success
    Frontend-->>User: Show confirmation`
  },
  {
    title: "Mind Map",
    type: "mindmap",
    description: "Project idea with branching concepts",
    code: `mindmap
    root((Project Plan))
        Features
            Core functionality
                Authentication
                Dashboard
                Reports
            Advanced features
                Analytics
                Integrations
                AI assistant
        Resources
            Development team
                Frontend
                Backend
                QA
            Budget
                Q1 allocation
                Q2 allocation
        Timeline
            Planning
            Development
            Testing
            Deployment`
  }
]; 